import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Select, Card, Typography, Checkbox, Button, notification } from 'antd';
import ReactECharts from 'echarts-for-react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { comparePropertiesArrays } from '../../redux/api/documents/propertiesComparisonThunks';
import { Document, ComparisonResult, PropertyType, PropertyFull } from '../../types';
import { selectDocumentsComputing, selectPropertyTypes } from '../../redux/api/documents/documentsSelectors';
import { colorBySimilarity } from '../../services/utils/colorUtils';
import { useSimSettings } from '../../hooks/useSimSettings';
import { exportHeatmapToXlsx } from '../../services/export/heatmapExcelExport';

const { Option } = Select;
const { Text } = Typography;

interface ComparisonHeatmapProps {
    documents: Record<string, Document>;
}

type HeatMapData = ComparisonResult[][];

interface HeatmapItem {
    value: [number, number, number];
    propAText: string;
    propBText: string;
    itemStyle: { color: string };
}

const ComparisonHeatmap: React.FC<ComparisonHeatmapProps> = ({ documents }) => {
    const dispatch = useAppDispatch();
    const propertyTypes = useAppSelector(selectPropertyTypes);
    const isComputing = useAppSelector(selectDocumentsComputing);
    const { calcSim, weightA } = useSimSettings();

    // Состояния выбора документов и типов свойств
    const [selectedDocIdA, setSelectedDocIdA] = useState<string | undefined>();
    const [selectedDocIdB, setSelectedDocIdB] = useState<string | undefined>();
    const [propertyTypeA, setPropertyTypeA] = useState<string | undefined>();
    const [propertyTypeB, setPropertyTypeB] = useState<string | undefined>();

    // Чекбокс для отображения средних значений
    const [showAverages, setShowAverages] = useState<boolean>(false);

    // Результаты сравнения
    const [heatMapData, setHeatMapData] = useState<HeatMapData>([]);
    const [exporting, setExporting] = useState(false);

    const canExport = heatMapData.length > 0 && heatMapData[0].length > 0;

    const propertyTypeName = (id: string | undefined) =>
        propertyTypes.find((pt) => pt.id === id)?.name ?? '';

    const handleExportExcel = async () => {
        if (!canExport || !selectedDocIdA || !selectedDocIdB) return;
        setExporting(true);
        try {
            await exportHeatmapToXlsx({
                heatMapData,
                showAverages,
                calcSim,
                metadata: {
                    documentTitleA: documents[selectedDocIdA]?.title ?? '',
                    documentTitleB: documents[selectedDocIdB]?.title ?? '',
                    propertyTypeNameA: propertyTypeName(propertyTypeA),
                    propertyTypeNameB: propertyTypeName(propertyTypeB),
                    exportedAt: new Date(),
                    weightA,
                },
            });
        } catch (err) {
            console.error('Heatmap Excel export failed:', err);
            notification.error({
                message: 'Ошибка экспорта',
                description: 'Не удалось сформировать файл Excel. Попробуйте ещё раз.',
            });
        } finally {
            setExporting(false);
        }
    };

    // Состояние для отображения информации о выбранной ячейке
    const [selectedCellInfo, setSelectedCellInfo] = useState<{
        propAText: string;
        propBText: string;
        similarity: number;
    } | null>(null);

    // ref для хранения индекса выделенной ячейки (чтобы не зависеть от состояния в useCallback)
    const selectedCellIndexRef = useRef<number | null>(null);

    // Запрос сравнения при наличии всех параметров
    useEffect(() => {
        if (!selectedDocIdA || !selectedDocIdB || !propertyTypeA || !propertyTypeB) return;

        const docA = documents[selectedDocIdA];
        const docB = documents[selectedDocIdB];
        if (!docA || !docB) return;

        const propsArrayA: PropertyFull[] =
            docA.properties?.filter(p => p.propertyTypeId === propertyTypeA) || [];
        const propsArrayB: PropertyFull[] =
            docB.properties?.filter(p => p.propertyTypeId === propertyTypeB) || [];

        dispatch(comparePropertiesArrays({ propsArrayA, propsArrayB }))
            .unwrap()
            .then((payload) => {
                setHeatMapData(payload);
                // Сбрасываем выделение при смене данных
                selectedCellIndexRef.current = null;
                setSelectedCellInfo(null);
            })
            .catch((error) => {
                console.error('Error comparing properties arrays:', error);
                setHeatMapData([]);
            });
    }, [selectedDocIdA, selectedDocIdB, propertyTypeA, propertyTypeB, documents, dispatch]);

    // Функция для обрезки строк
    const truncate = (text: string, maxLength: number): string =>
        text.length > maxLength ? text.slice(0, maxLength) + '...' : text;

    // Массив для heatmap
    const heatmapDataItems = useMemo<HeatmapItem[]>(() => {
        if (heatMapData.length === 0) return [];
        return heatMapData.flatMap((row, i) =>
            row.map((cell, j) => {
                const sim = calcSim(cell.similarity);
                return {
                    value: [j, i, sim],
                    propAText: cell.propA.text,
                    propBText: cell.propB.text,
                    itemStyle: { color: colorBySimilarity(sim) },
                };
            })
        );
    }, [heatMapData]);

    // Подписи осей (исходные, без средних)
    const baseXLabels = useMemo(
        () =>
            heatMapData.length > 0
                ? heatMapData[0].map((cell) => truncate(cell.propB.text, 20))
                : [],
        [heatMapData]
    );
    const baseYLabels = useMemo(
        () =>
            heatMapData.length > 0
                ? heatMapData.map((row) => truncate(row[0].propA.text, 20))
                : [],
        [heatMapData]
    );

    // Вычисляем средние значения по столбцам
    const columnAverages = useMemo(() => {
        if (heatMapData.length === 0) return [];
        const numRows = heatMapData.length;
        const numCols = heatMapData[0].length;
        const avgs = [];
        for (let j = 0; j < numCols; j++) {
            let sum = 0;
            for (let i = 0; i < numRows; i++) {
                sum += calcSim(heatMapData[i][j].similarity);
            }
            avgs.push(sum / numRows);
        }
        return avgs;
    }, [heatMapData]);

    // Вычисляем средние значения по строкам
    const rowAverages = useMemo(() => {
        if (heatMapData.length === 0) return [];
        const numRows = heatMapData.length;
        const numCols = heatMapData[0].length;
        const avgs = [];
        for (let i = 0; i < numRows; i++) {
            let sum = 0;
            for (let j = 0; j < numCols; j++) {
                sum += calcSim(heatMapData[i][j].similarity);
            }
            avgs.push(sum / numCols);
        }
        return avgs;
    }, [heatMapData]);

    // Генерируем метки осей с учётом флага showAverages
    const xLabels = useMemo(() => {
        if (!showAverages) return baseXLabels;
        return baseXLabels.map((label, index) => {
            const avg = columnAverages[index]?.toFixed(2) ?? '0.00';
            // Пример: выводим в две строки: первая - исходный label, вторая - "avg: ...".
            // Если нужно короче - уберите перенос строки.
            return `${label}\n(${avg})`;
        });
    }, [showAverages, baseXLabels, columnAverages]);

    const yLabels = useMemo(() => {
        if (!showAverages) return baseYLabels;
        return baseYLabels.map((label, index) => {
            const avg = rowAverages[index]?.toFixed(2) ?? '0.00';
            return `${label} (${avg})`;
        });
    }, [showAverages, baseYLabels, rowAverages]);

    // Конфигурация графика
    const option = useMemo(() => {
        if (heatMapData.length === 0) return {};
        return {
            responsive: true,
            maintainAspectRatio: false,
            tooltip: {
                position: 'top',
                confine: true,
                formatter: (params: any) => {
                    if (!params || !params.data) return '';
                    return `
            <div>Свойство A: ${params.data.propAText}</div>
            <div>Свойство B: ${params.data.propBText}</div>
            <div>Близость: ${params.data.value[2].toFixed(2)}</div>
          `;
                },
            },
            animationDurationUpdate: 300,
            grid: {
                height: '80%',
                top: '10%',
            },
            xAxis: {
                type: 'category',
                // Если подписи станут слишком большими, можно добавить rotation
                // axisLabel: { rotate: -45 },
                data: xLabels,
                splitArea: { show: true },
            },
            yAxis: {
                type: 'category',
                data: yLabels,
                splitArea: { show: true },
            },
            visualMap: {
                min: 0,
                max: 1,
                calculable: true,
                orient: 'vertical',
                right: '10',
                top: 'middle',
            },
            series: [
                {
                    name: 'Близость',
                    type: 'heatmap',
                    data: heatmapDataItems,
                    label: { show: false },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowColor: 'rgba(0, 0, 0, 0.5)',
                        },
                    },
                },
            ],
        };
    }, [heatMapData, heatmapDataItems, xLabels, yLabels]);

    // Обработчик клика по ячейке (highlight / downplay)
    const onChartClick = useCallback((params: any, chart: any) => {
        console.log(params);
        if (!params || !params.data) return;
        const clickedIndex: number = params.dataIndex;
        if (selectedCellIndexRef.current === clickedIndex) {
            chart.dispatchAction({
                type: 'downplay',
                seriesIndex: 0,
                dataIndex: clickedIndex,
            });
            selectedCellIndexRef.current = null;
            setSelectedCellInfo(null);
        } else {
            if (selectedCellIndexRef.current !== null) {
                chart.dispatchAction({
                    type: 'downplay',
                    seriesIndex: 0,
                    dataIndex: selectedCellIndexRef.current,
                });
            }
            chart.dispatchAction({
                type: 'highlight',
                seriesIndex: 0,
                dataIndex: clickedIndex,
            });
            selectedCellIndexRef.current = clickedIndex;
            const { propAText, propBText, value } = params.data;
            setSelectedCellInfo({
                propAText,
                propBText,
                similarity: value[2],
            });
        }
    }, []);

    // Стабильный объект onEvents
    const [onEventsState, setOnEventsState] = useState({ click: onChartClick });
    useEffect(() => {
        setOnEventsState({ click: onChartClick });
    }, [onChartClick]);

    return (
        <Card
            title="Тепловая карта сходства свойств (ECharts)"
            style={{ height: '100%' }}
            extra={
                <Button
                    type="primary"
                    loading={exporting}
                    disabled={!canExport}
                    onClick={handleExportExcel}
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                    Экспорт в Excel
                </Button>
            }
        >
            {/* Фильтры и чекбокс */}
            <div className="flex flex-wrap w-full mb-4 gap-4">
                <div className="flex-1 min-w-[200px]">
                    <Text className="block mb-1">Документ A</Text>
                    <Select
                        value={selectedDocIdA}
                        className="w-full"
                        placeholder="Выберите документ A"
                        onChange={(value) => setSelectedDocIdA(value)}
                        allowClear
                    >
                        {Object.entries(documents).map(([docId, doc]) => (
                            <Option key={docId} value={docId}>
                                {doc.title}
                            </Option>
                        ))}
                    </Select>
                </div>
                <div className="flex-1 min-w-[200px]">
                    <Text className="block mb-1">Тип свойства A</Text>
                    <Select
                        value={propertyTypeA}
                        className="w-full"
                        placeholder="Выберите тип свойств A"
                        onChange={(value) => setPropertyTypeA(value)}
                        allowClear
                    >
                        {propertyTypes.map((pt: PropertyType) => (
                            <Option key={pt.id} value={pt.id}>
                                {pt.name}
                            </Option>
                        ))}
                    </Select>
                </div>
                <div className="flex-1 min-w-[200px]">
                    <Text className="block mb-1">Документ B</Text>
                    <Select
                        value={selectedDocIdB}
                        className="w-full"
                        placeholder="Выберите документ B"
                        onChange={(value) => setSelectedDocIdB(value)}
                        allowClear
                    >
                        {Object.entries(documents).map(([docId, doc]) => (
                            <Option key={docId} value={docId}>
                                {doc.title}
                            </Option>
                        ))}
                    </Select>
                </div>
                <div className="flex-1 min-w-[200px]">
                    <Text className="block mb-1">Тип свойства B</Text>
                    <Select
                        value={propertyTypeB}
                        className="w-full"
                        placeholder="Выберите тип свойств B"
                        onChange={(value) => setPropertyTypeB(value)}
                        allowClear
                    >
                        {propertyTypes.map((pt: PropertyType) => (
                            <Option key={pt.id} value={pt.id}>
                                {pt.name}
                            </Option>
                        ))}
                    </Select>
                </div>
                {/* Чекбокс для отображения средних значений */}
                <div className="flex items-center">
                    <Checkbox
                        checked={showAverages}
                        onChange={(e) => setShowAverages(e.target.checked)}
                    />
                    <Text className="ml-1">Показать средние значения</Text>
                </div>
            </div>

            {/* Контейнер для графика */}
            <div style={{ overflowX: 'hidden' }}>
                {isComputing ? (
                    <div>Выполняется вычисление...</div>
                ) : (
                    <ReactECharts
                        option={option}
                        style={{ height: '65vh', width: '100%' }}
                        notMerge={true}
                        lazyUpdate={true}
                        onEvents={onEventsState}
                    />
                )}
            </div>

            {/* Отображение информации о выбранной ячейке */}
            {selectedCellInfo && (
                <div className="mt-4 p-2 border border-gray-300 rounded">
                    <p>
                        <strong>Свойство A:</strong> {selectedCellInfo.propAText}
                    </p>
                    <p>
                        <strong>Свойство B:</strong> {selectedCellInfo.propBText}
                    </p>
                    <p>
                        <strong>Сходство:</strong> {selectedCellInfo.similarity.toFixed(2)}
                    </p>
                </div>
            )}
        </Card>
    );
};

export default React.memo(ComparisonHeatmap);
